/**
 * SmartShopperChat — floating shopping assistant widget.
 *
 * Mounted globally by Layout.jsx for logged-in buyers. Renders as a
 * pinned button bottom-right; clicking pops open a chat panel with
 * conversation history, streaming replies, inline product cards, and
 * a voice mic. State is scoped to a single session_id kept in
 * sessionStorage so refreshing the page preserves the thread.
 *
 * The assistant returns product references via the backend's
 * <prod:PID>-parsing pipeline, so this widget just renders the
 * pre-hydrated `products[]` array as inline cards.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Loader2, RotateCw,
         ShoppingBag, Heart, Store, Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import VoiceMicButton from "./VoiceMicButton";


const SESSION_KEY_PREFIX = "shoprizz.assistant.sid.";

// Persona-scoped UX config. Buyer, seller and admin see different
// headers, quick prompts, placeholder copy and Cart/Save affordances.
const ROLE_UX = {
  buyer: {
    header: "Shop assistant",
    hint: "Powered by Claude · Try, compare, buy",
    placeholder: "Ask about products, compare, or buy…",
    empty: "I help you find, compare and buy on ShopRizz.",
    quickPrompts: [
      "What's trending in Fashion?",
      "Show me items I can rent for a wedding",
      "Compare your top 2 rated products",
      "Any deals under $50?",
    ],
    showCartSave: true,
  },
  seller: {
    header: "Seller copilot",
    hint: "Powered by Claude · Copy, pricing, ops",
    placeholder: "Ask for copy, pricing tips, or ops help…",
    empty: "I help you write descriptions, price smartly, and run your shop.",
    quickPrompts: [
      "Write a description for my top product",
      "Which of my products need attention?",
      "How do I go live right now?",
      "Explain rental pricing overrides",
    ],
    showCartSave: false,
  },
  admin: {
    header: "Ops copilot",
    hint: "Powered by Claude · Moderate, analyse",
    placeholder: "Ask about KYC, refunds, moderation…",
    empty: "I help you moderate, analyse, and operate the platform.",
    quickPrompts: [
      "Which sellers need KYC review?",
      "Show me low-rated products to flag",
      "How do I pause rentals platform-wide?",
      "Explain the refund flow",
    ],
    showCartSave: false,
  },
};


function ProductCard({ p, onAddToCart, onSave, showCartSave = true }) {
  // Compact rich card rendered inline inside an assistant reply. Uses
  // /product/{id} deep-link so buyers can dig deeper in a full page.
  return (
    <div className="bg-[#1A0F1F] border border-[#3D2845] rounded-xl overflow-hidden flex gap-2 p-2"
      data-testid={`asst-product-${p.product_id}`}>
      <Link to={`/product/${p.product_id}`} className="shrink-0">
        {p.image
          ? <img src={p.image} alt={p.name} className="w-16 h-20 object-cover rounded"/>
          : <div className="w-16 h-20 bg-[#3D2845] rounded"/>}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/product/${p.product_id}`}
          className="font-bold text-xs line-clamp-2 leading-tight hover:text-[#D4FF00]">
          {p.name}
        </Link>
        <div className="flex items-center gap-1 mt-0.5 text-[10px] text-[#A1A1AA]">
          {p.store_name && <><Store size={9}/> <span className="truncate">{p.store_name}</span></>}
        </div>
        <div className="flex items-center justify-between mt-1">
          <div>
            <span className="text-[#D4FF00] font-display font-black text-sm">{fmtMoney(p.price)}</span>
            {p.rental_enabled && p.rental_per_day && (
              <span className="text-[9px] text-[#00F0FF] ml-1">· {fmtMoney(p.rental_per_day)}/day</span>
            )}
          </div>
          <div className="text-[10px] text-[#D4FF00] flex items-center gap-0.5">
            <Star size={9} fill="#D4FF00"/>{(p.rating || 5).toFixed(1)}
          </div>
        </div>
        {showCartSave ? (
          <div className="flex gap-1 mt-1.5">
            <button onClick={() => onAddToCart(p)}
              data-testid={`asst-add-cart-${p.product_id}`}
              className="text-[9px] font-bold uppercase tracking-wider bg-[#D4FF00] text-black px-2 py-0.5 rounded-full hover:bg-[#00F0FF] transition inline-flex items-center gap-1">
              <ShoppingBag size={9}/> Cart
            </button>
            <button onClick={() => onSave(p)}
              data-testid={`asst-save-${p.product_id}`}
              className="text-[9px] font-bold uppercase tracking-wider bg-[#3D2845] text-white px-2 py-0.5 rounded-full hover:bg-[#FF007F] transition inline-flex items-center gap-1">
              <Heart size={9}/> Save
            </button>
          </div>
        ) : (
          <div className="flex gap-1 mt-1.5">
            <Link to={`/product/${p.product_id}`}
              data-testid={`asst-view-${p.product_id}`}
              className="text-[9px] font-bold uppercase tracking-wider bg-[#3D2845] text-white px-2 py-0.5 rounded-full hover:bg-[#D4FF00] hover:text-black transition">
              Open
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}


function Bubble({ turn, onAddToCart, onSave, showCartSave }) {
  const isUser = turn.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
        isUser
          ? "bg-[#D4FF00] text-black font-medium rounded-tr-sm"
          : "bg-[#261534] text-[#FAFAFA] rounded-tl-sm"
      }`}>
        {turn.text && <div className="whitespace-pre-wrap">{turn.text}</div>}
        {(turn.products || []).length > 0 && (
          <div className={`space-y-2 ${turn.text ? "mt-2" : ""}`}>
            {turn.products.map(p => (
              <ProductCard key={p.product_id} p={p}
                onAddToCart={onAddToCart} onSave={onSave}
                showCartSave={showCartSave}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function SmartShopperChat() {
  const { user } = useAuth();
  const { addItem } = useCart();
  const [open, setOpen] = useState(false);
  // Track the identity (user_id + role) this chat state belongs to.
  // When either changes (profile switch, logout, role upgrade), we
  // wipe the in-memory conversation so nothing leaks across users.
  const role = (user?.role || "buyer").toLowerCase();
  const ux = ROLE_UX[role] || ROLE_UX.buyer;
  const identityKey = user?.user_id ? `${user.user_id}|${role}` : "";
  const currentIdentityRef = useRef(identityKey);
  // Session id is scoped by user_id — every user has their own storage key.
  const storageKey = user?.user_id ? `${SESSION_KEY_PREFIX}${user.user_id}` : null;
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined" || !user?.user_id) return "";
    return sessionStorage.getItem(`${SESSION_KEY_PREFIX}${user.user_id}`) || "";
  });
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  const scrollRef = useRef(null);

  // Wipe chat state when the authenticated identity changes (profile
  // switch, logout → login as someone else, role upgrade, or plain logout).
  // Belt & suspenders: track both user_id and role; either change triggers
  // a full reset so nothing from a previous session ever renders.
  useEffect(() => {
    if (currentIdentityRef.current !== identityKey) {
      setTurns([]);
      setInput("");
      setOpen(false);
      setSending(false);
      setToast("");
      const nextId = user?.user_id || null;
      setSessionId(nextId && typeof window !== "undefined"
        ? sessionStorage.getItem(`${SESSION_KEY_PREFIX}${nextId}`) || ""
        : "");
      currentIdentityRef.current = identityKey;
    }
  }, [identityKey, user?.user_id]);

  // Auto-scroll to newest bubble on new turn.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, open]);

  const send = useCallback(async (text) => {
    const q = (text || input || "").trim();
    if (!q || sending) return;
    setInput("");
    const userKey = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setTurns(prev => [...prev, { _key: userKey, role: "user", text: q, products: [] }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: q,
        session_id: sessionId || undefined,
        context: {
          page: typeof window !== "undefined" ? window.location.pathname : undefined,
        },
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        if (storageKey) sessionStorage.setItem(storageKey, data.session_id);
      }
      setTurns(prev => [...prev, {
        _key: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: "assistant",
        text: data.reply,
        products: data.products || [],
        actions: data.actions || [],
      }]);
    } catch (e) {
      setTurns(prev => [...prev, {
        _key: `e-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: "assistant",
        text: formatApiError(e?.response?.data?.detail) || "Hmm, something went wrong. Try again?",
        products: [],
      }]);
    } finally { setSending(false); }
  }, [input, sending, sessionId, storageKey]);

  const reset = async () => {
    if (sessionId) {
      try { await api.post(`/assistant/session/${sessionId}/reset`); } catch { /* ignore */ }
    }
    setTurns([]); setInput("");
  };

  const handleAddToCart = async (p) => {
    // If product has size/color choices, tell buyer to open the product
    // page for full picker — the widget stays minimal.
    const needsPick = (p.sizes || []).length > 0 || (p.colors || []).length > 0;
    if (needsPick) {
      setToast(`Open ${p.name} to pick size / colour`);
      setTimeout(() => setToast(""), 3000);
      window.location.href = `/product/${p.product_id}`;
      return;
    }
    try {
      await addItem(p.product_id, null, 1, null);
      setToast(`Added "${p.name}" to cart`);
      setTimeout(() => setToast(""), 2500);
    } catch {
      setToast("Add failed"); setTimeout(() => setToast(""), 2000);
    }
  };
  const handleSave = async (p) => {
    try {
      await api.post(`/me/save/product/${p.product_id}`);
      setToast(`Saved "${p.name}"`);
      setTimeout(() => setToast(""), 2500);
    } catch {
      setToast("Save failed"); setTimeout(() => setToast(""), 2000);
    }
  };

  // Visible to any authenticated user (buyer, seller, admin) so
  // internal folks can also demo it. Hidden only on auth pages to
  // keep the login hero clean.
  if (!user) return null;
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/auth/")) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button onClick={() => setOpen(true)}
          data-testid="asst-fab-open"
          className="fixed bottom-24 right-5 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-[#D4FF00] to-[#00F0FF] text-black shadow-2xl flex items-center justify-center hover:scale-105 transition"
          aria-label="Open shopping assistant">
          <Sparkles size={22}/>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-4 z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-8rem)] bg-[#0F0817] border border-[#3D2845] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="asst-panel">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#3D2845] bg-gradient-to-r from-[#D4FF00]/10 to-[#00F0FF]/10">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4FF00] to-[#00F0FF] flex items-center justify-center">
              <Sparkles size={14} className="text-black"/>
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase text-sm leading-none"
                data-testid="asst-header-title">{ux.header}</div>
              <div className="text-[9px] text-[#A1A1AA] uppercase tracking-widest">
                {ux.hint}
              </div>
            </div>
            <button onClick={reset} title="New conversation"
              data-testid="asst-reset"
              className="w-7 h-7 rounded-full bg-[#3D2845] hover:bg-[#FF007F] text-white flex items-center justify-center transition">
              <RotateCw size={11}/>
            </button>
            <button onClick={() => setOpen(false)}
              data-testid="asst-close"
              className="w-7 h-7 rounded-full bg-[#3D2845] hover:bg-[#FF007F] text-white flex items-center justify-center transition">
              <X size={12}/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3"
            data-testid="asst-messages">
            {turns.length === 0 && (
              <div className="text-center py-4">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-[#D4FF00] to-[#00F0FF] flex items-center justify-center mb-3">
                  <Sparkles size={22} className="text-black"/>
                </div>
                <div className="font-display font-black text-lg uppercase mb-1">Hey {user.name || "there"} 👋</div>
                <div className="text-xs text-[#A1A1AA] mb-4">
                  {ux.empty}<br/>Try one of these:
                </div>
                <div className="space-y-1.5">
                  {ux.quickPrompts.map((p, i) => (
                    <button key={p} onClick={() => send(p)}
                      data-testid={`asst-quick-${i}`}
                      className="w-full text-left text-xs bg-[#261534] hover:bg-[#3D2845] border border-[#3D2845] hover:border-[#D4FF00] px-3 py-2 rounded-lg transition">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t) => (
              <Bubble key={t._key} turn={t}
                onAddToCart={handleAddToCart} onSave={handleSave}
                showCartSave={ux.showCartSave}/>
            ))}
            {sending && (
              <div className="flex justify-start mb-3">
                <div className="bg-[#261534] rounded-2xl rounded-tl-sm px-3 py-2 flex items-center gap-2 text-xs text-[#A1A1AA]"
                  data-testid="asst-typing">
                  <Loader2 size={12} className="animate-spin"/> thinking…
                </div>
              </div>
            )}
          </div>

          {/* Toast */}
          {toast && (
            <div className="px-3 pb-2 text-[10px] text-center text-[#D4FF00]"
              data-testid="asst-toast">{toast}</div>
          )}

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#3D2845] p-2 flex items-center gap-1.5 bg-[#0F0817]">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={ux.placeholder}
              data-testid="asst-input"
              disabled={sending}
              className="flex-1 bg-[#1A0F1F] border border-[#3D2845] focus:border-[#D4FF00] rounded-full px-3 py-2 text-sm outline-none"/>
            <VoiceMicButton
              testid="asst-voice"
              onTranscript={(t) => setInput(t)}
              size={13} className="w-9 h-9"/>
            <button type="submit" disabled={!input.trim() || sending}
              data-testid="asst-send"
              className="w-9 h-9 rounded-full bg-[#D4FF00] hover:bg-[#00F0FF] text-black flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition">
              <Send size={13}/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
